import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import { useCreaOrdine, useDestinazioniCliente } from '../api/ordini-scrittura.js';
import { Modale } from '../componenti/Modale.js';
import { SchedaDestinazione, voceDestinazione } from '../componenti/SchedaDestinazione.js';
import { giornoLocale } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Un ordine nuovo.
 *
 * Nasce vuoto nel primo stato: gli articoli si aggiungono dopo, dalla scheda.
 * È come lavora il v2 ed è come si lavora davvero — al telefono il cliente
 * detta numero e data, gli articoli arrivano con la mail.
 *
 * Il cliente si cerca, non si sceglie da una tendina: sono più di mille.
 * Scelto il cliente, le destinazioni sono le sue, e la prima proposta è la
 * sede — che è quella giusta quasi sempre.
 */
export function ModaleNuovoOrdine({ onChiudi }: { onChiudi: () => void }) {
  const navigate = useNavigate();
  const crea = useCreaOrdine();

  const [cerca, setCerca] = useState('');
  const [cliente, setCliente] = useState<{ id: number; name: string } | null>(null);
  const [campi, setCampi] = useState({
    addressId: '',
    orderNumber: '',
    orderDate: giornoLocale(),
    deliveryDateRequested: '',
    paymentTypeId: '',
    bankId: '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  // la ricerca parte da due lettere: aprire la finestra non deve caricare
  // milleduecento clienti che nessuno ha chiesto
  const clienti = useQuery({
    queryKey: ['clienti-per-ordine', cerca],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string; bcCodeSpa: string | null }[] }>(
        `/api/identities?type=customer&size=15&q=${encodeURIComponent(cerca)}`,
      ),
    enabled: cerca.trim().length >= 2,
  });

  const destinazioni = useDestinazioniCliente(cliente?.id ?? null);
  // la sede del cliente è la destinazione di partenza: prima andava scelta
  // ogni volta, anche per i clienti che hanno un indirizzo solo
  const sede = destinazioni.data?.destinations.find((d) => d.isMain);
  const indirizzoScelto = campi.addressId || (sede ? String(sede.id) : '');

  /**
   * Il sezionale non si sceglie: è quello del cliente, come nel v2, perché
   * decide a quale società di Business Central va l'ordine — e nella Inc è
   * sempre INC (vedi `sezionaleDiNuovoOrdine` nel servizio). Si mostra prima
   * di creare, così chi scopre che manca lo sistema subito.
   */
  const predefiniti = useQuery({
    queryKey: ['predefiniti-ordine', cliente?.id],
    queryFn: () =>
      chiama<{ sectionalId: number | null; sectional: string | null }>(
        `/api/customers/${cliente!.id}/order-defaults`,
      ),
    enabled: cliente !== null,
  });
  const senzaSezionale = predefiniti.data !== undefined && predefiniti.data.sectionalId === null;

  /**
   * Una destinazione che la Business Central dell'ordine non ha, in BC non si
   * trova. Nella Inc non si vedono proprio (lo fa il servizio); nella SPA si
   * vedono tutte, e qui si avvisa. Solo se di questo cliente si sa già dove
   * sta ogni destinazione: prima del primo import non si sa.
   */
  const destinazioneScelta = destinazioni.data?.destinations.find((d) => String(d.id) === indirizzoScelto);
  const societaOrdine = predefiniti.data?.sectional?.toUpperCase() ?? null;
  const siSaDoveSono = (destinazioni.data?.destinations ?? []).some((d) => d.bc.length > 0);
  // l'indirizzo principale in BC è quello del cliente stesso: lo trova sempre
  const fuoriDallaBc =
    destinazioneScelta &&
    !destinazioneScelta.isMain &&
    societaOrdine &&
    siSaDoveSono &&
    !destinazioneScelta.bc.includes(societaOrdine)
      ? societaOrdine
      : null;

  /** Sezionali, tipi di pagamento e banche: le scelte fisse della testata. */
  const opzioni = useQuery({
    queryKey: ['opzioni-ordine'],
    queryFn: () =>
      chiama<{
        options: Record<string, { id: string | number; label: string }[]>;
      }>('/api/orders/filters'),
    staleTime: 10 * 60_000,
  });

  const banche = useQuery({
    queryKey: ['banche'],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string }[] }>('/api/reference-data/banks?size=100'),
    staleTime: 10 * 60_000,
  });

  const errore = crea.error instanceof ErroreApi ? crea.error.message : null;
  const completo =
    cliente !== null &&
    predefiniti.data?.sectionalId != null &&
    campi.orderNumber.trim() !== '' &&
    campi.orderDate !== '';

  // il secondo clic di un doppio clic arriva prima che il pulsante si spenga:
  // lo ferma questa bandierina, che cambia subito e non aspetta il disegno
  const inviato = useRef(false);
  const conferma = () => {
    if (inviato.current) return;
    inviato.current = true;
    crea.mutate(
      {
        customerId: cliente!.id,
        addressId: indirizzoScelto ? Number(indirizzoScelto) : null,
        orderNumber: campi.orderNumber.trim(),
        orderDate: campi.orderDate,
        deliveryDateRequested: campi.deliveryDateRequested || null,
        ...(campi.paymentTypeId ? { paymentTypeId: Number(campi.paymentTypeId) } : {}),
        ...(campi.bankId ? { bankId: Number(campi.bankId) } : {}),
      },
      {
        onSuccess: ({ id }) => {
          onChiudi();
          // si entra subito nell'ordine: il passo dopo è metterci gli articoli
          void navigate({ to: '/sales-orders/$id', params: { id: String(id) } });
        },
        // se non è partito, si può riprovare
        onError: () => {
          inviato.current = false;
        },
      },
    );
  };

  return (
    <Modale
      titolo={t('Nuovo ordine')}
      nota={t('L\'ordine nasce vuoto nel primo stato: gli articoli si aggiungono dopo, dalla sua scheda.')}
      larga
      inCorso={crea.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={crea.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!completo || crea.isPending}
          >
            {crea.isPending ? 'Creo…' : "Crea l'ordine"}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label htmlFor="no-cliente">
          {t('Cliente')}<span className="req">*</span>
          <span className="dim" style={{ fontWeight: 400 }}>
            {' '}
            {t('— cerca per nome, codice BC o partita IVA')}
          </span>
        </label>
        {cliente ? (
          <div className="scelto">
            <strong>{cliente.name}</strong>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setCliente(null);
                cambia('addressId', '');
              }}
            >
              {t('Cambia')}
            </button>
          </div>
        ) : (
          <>
            <input
              id="no-cliente"
              className="input"
              placeholder={t('Scrivi almeno due lettere')}
              value={cerca}
              autoFocus
              onChange={(e) => setCerca(e.target.value)}
            />
            {(clienti.data?.rows ?? []).length > 0 && (
              <ul className="risultati">
                {clienti.data!.rows.map((c) => (
                  <li key={c.id}>
                    <button
                      className="risultato"
                      onClick={() => {
                        setCliente({ id: c.id, name: c.name });
                        setCerca('');
                      }}
                    >
                      <span>{c.name}</span>
                      {c.bcCodeSpa && <span className="dim num">{c.bcCodeSpa}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="fields">
        {/* tutta la riga: con la via, la voce della tendina è lunga */}
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          {/* non è obbligatoria: senza, vale la sede, e a un cliente senza
              indirizzi l'ordine si fa lo stesso (Antonio, 27 ottobre 2026) */}
          <label htmlFor="no-dest">{t('Destinazione della merce')}</label>
          <select
            id="no-dest"
            className="select"
            value={indirizzoScelto}
            disabled={cliente === null}
            onChange={(e) => cambia('addressId', e.target.value)}
          >
            <option value="">
              {cliente ? t('Nessuna: in BC va l’indirizzo del cliente') : t('Prima scegli il cliente')}
            </option>
            {(destinazioni.data?.destinations ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {voceDestinazione(d)}
              </option>
            ))}
          </select>
          {destinazioneScelta && <SchedaDestinazione destinazione={destinazioneScelta} />}
          {cliente && destinazioni.data?.destinations.length === 0 && (
            <p className="hint" style={{ margin: '6px 0 0' }}>
              {t('Questo cliente non ha indirizzi: l’ordine nasce senza destinazione, e Business Central spedisce all’indirizzo del cliente.')}
            </p>
          )}
          {fuoriDallaBc && (
            <p className="hint" style={{ color: 'var(--tomato)', margin: '6px 0 0' }}>
              {fuoriDallaBc === 'INC'
                ? t('Questa destinazione non c’è nella Business Central della Inc, dove va l’ordine: lì non la troverebbe. Va creata in BC.')
                : t('Questa destinazione non c’è nella Business Central della SPA, dove va l’ordine: lì non la troverebbe. Va creata in BC.')}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="no-po">
            {t('Numero d’ordine del cliente')}<span className="req">*</span>
          </label>
          <input
            id="no-po"
            className="input"
            value={campi.orderNumber}
            onChange={(e) => cambia('orderNumber', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="no-sez">{t('Sezionale')}</label>
          <div id="no-sez" style={{ paddingTop: 6 }}>
            {cliente === null ? (
              <span className="dim">{t('quello del cliente')}</span>
            ) : senzaSezionale ? (
              <span style={{ color: 'var(--tomato)', fontSize: 13 }}>
                {t('Questo cliente non ha un sezionale: impostalo nelle sue «Preferenze commerciali».')}
              </span>
            ) : (
              <strong>{predefiniti.data?.sectional ?? '…'}</strong>
            )}
          </div>
        </div>

        <div className="field">
          <label htmlFor="no-data">
            {t('Data dell’ordine')}<span className="req">*</span>
          </label>
          <input
            id="no-data"
            className="input"
            type="date"
            value={campi.orderDate}
            onChange={(e) => cambia('orderDate', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="no-consegna">{t('Consegna richiesta')}</label>
          <input
            id="no-consegna"
            className="input"
            type="date"
            value={campi.deliveryDateRequested}
            onChange={(e) => cambia('deliveryDateRequested', e.target.value)}
          />
        </div>

        {/* pagamento e banca si prendono dalle preferenze del cliente: si
            scelgono qui solo se quel cliente non ne ha */}
        <div className="field">
          <label htmlFor="no-pag">{t('Tipo di pagamento')}</label>
          <select
            id="no-pag"
            className="select"
            value={campi.paymentTypeId}
            onChange={(e) => cambia('paymentTypeId', e.target.value)}
          >
            <option value="">{t('Quello del cliente')}</option>
            {(opzioni.data?.options.paymentTypes ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="no-banca">{t('Banca')}</label>
          <select
            id="no-banca"
            className="select"
            value={campi.bankId}
            onChange={(e) => cambia('bankId', e.target.value)}
          >
            <option value="">{t('Quella del cliente')}</option>
            {(banche.data?.rows ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modale>
  );
}
